import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Video, Calendar, Clock, User, Check, X, Loader2, 
  Phone, Mic, MicOff, VideoOff, AlertCircle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TelemedicineBooking = ({ userId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [reason, setReason] = useState('');
  const [step, setStep] = useState(1); // 1: select date, 2: select slot, 3: confirm
  const [myAppointments, setMyAppointments] = useState([]);
  const [activeSession, setActiveSession] = useState(null);

  useEffect(() => {
    loadMyAppointments();
  }, []);

  const loadMyAppointments = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/telemedicine/my-appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAppointments(res.data.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const loadSlots = async (date) => {
    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      const res = await axios.get(`${API}/telemedicine/slots/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(res.data.slots || []);
    } catch (error) {
      console.error('Error loading slots:', error);
      toast.error(error.response?.data?.detail || 'Errore caricamento slot');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedSlot(null);
    setSelectedDoctor(null);
    loadSlots(date);
    setStep(2);
  };

  const handleSlotSelect = (slot, doctor) => {
    setSelectedSlot(slot);
    setSelectedDoctor(doctor);
    setStep(3);
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedSlot || !reason.trim()) {
      toast.error('Compila tutti i campi');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      await axios.post(`${API}/telemedicine/book`, {
        date: selectedDate,
        slot_time: selectedSlot.start_time,
        reason: reason,
        doctor_id: selectedDoctor?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Videoconsulto prenotato con successo!');
      loadMyAppointments();
      setStep(1);
      setSelectedDate('');
      setSelectedSlot(null);
      setReason('');
    } catch (error) {
      console.error('Error booking:', error);
      toast.error(error.response?.data?.detail || 'Errore prenotazione');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = async (appointment) => {
    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      const res = await axios.post(`${API}/telemedicine/${appointment.id}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open video room
      setActiveSession({
        ...appointment,
        room_url: res.data.room_url,
        token: res.data.token
      });
      
    } catch (error) {
      console.error('Error joining:', error);
      toast.error(error.response?.data?.detail || 'Errore accesso videoconsulto');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Sei sicuro di voler annullare questo videoconsulto?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/telemedicine/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Videoconsulto annullato');
      loadMyAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore annullamento');
    }
  };

  // Get available dates (next 30 days)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return `${days[date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`;
  };

  // Video Session View
  if (activeSession) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        <div className="bg-[#1A1A1A] p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6 text-[#2A9FCB]" />
            <div>
              <h3 className="text-white font-semibold">Videoconsulto in corso</h3>
              <p className="text-gray-400 text-sm">{activeSession.doctor_name}</p>
            </div>
          </div>
          <button
            onClick={() => setActiveSession(null)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Esci
          </button>
        </div>
        
        <div className="flex-1 flex items-center justify-center">
          {activeSession.room_url ? (
            <iframe
              src={`${activeSession.room_url}?t=${activeSession.token || ''}`}
              allow="camera; microphone; fullscreen; display-capture"
              className="w-full h-full"
              style={{ border: 'none' }}
            />
          ) : (
            <div className="text-center text-white">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
              <p>Connessione in corso...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[#2A9FCB]/10 flex items-center justify-center">
          <Video className="w-6 h-6 text-[#2A9FCB]" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-[#2A9FCB]" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            Videoconsulto
          </h2>
          <p className="text-sm text-gray-500">Consulti video gratuiti con i nostri specialisti</p>
        </div>
      </div>

      {/* My Appointments */}
      {myAppointments.length > 0 && (
        <div className="rounded-xl p-5 bg-[#F0FDF4] border border-green-200">
          <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            I Miei Videoconsulti
          </h3>
          <div className="space-y-3">
            {myAppointments.map(apt => (
              <div key={apt.id} className="bg-white rounded-lg p-4 border border-green-100 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#1A1A1A]">
                    {formatDate(apt.scheduled_date)} - {apt.slot_time}
                  </p>
                  <p className="text-sm text-gray-500">{apt.doctor_name}</p>
                  <p className="text-xs text-gray-400 mt-1">{apt.reason}</p>
                </div>
                <div className="flex gap-2">
                  {apt.status === 'scheduled' && (
                    <>
                      <button
                        onClick={() => handleJoinSession(apt)}
                        className="px-4 py-2 bg-[#2A9FCB] text-white rounded-lg text-sm font-medium hover:bg-[#1C729E] flex items-center gap-2"
                        data-testid="join-video-btn"
                      >
                        <Video className="w-4 h-4" />
                        Entra
                      </button>
                      <button
                        onClick={() => handleCancelAppointment(apt.id)}
                        className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {apt.status === 'completed' && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                      Completato
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Flow */}
      <div className="rounded-xl p-6 bg-[#F9FAFB] border border-gray-200">
        <h3 className="font-bold text-[#2A9FCB] mb-4">Prenota Videoconsulto Gratuito</h3>
        
        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= s ? 'bg-[#2A9FCB] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 ${step > s ? 'bg-[#2A9FCB]' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Date */}
        {step >= 1 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Seleziona Data
            </label>
            <select
              value={selectedDate}
              onChange={handleDateSelect}
              className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
              data-testid="select-date"
            >
              <option value="">Scegli una data...</option>
              {getAvailableDates().map(date => (
                <option key={date} value={date}>{formatDate(date)}</option>
              ))}
            </select>
          </div>
        )}

        {/* Step 2: Select Slot */}
        {step >= 2 && selectedDate && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-2" />
              Seleziona Orario (slot da 10 minuti)
            </label>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#2A9FCB]" />
              </div>
            ) : slots.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {slots.map(slot => (
                  <div key={slot.start_time} className="space-y-2">
                    <div className="text-center font-medium text-gray-700 text-sm">
                      {slot.start_time} - {slot.end_time}
                    </div>
                    {slot.doctors.length > 0 ? (
                      slot.doctors.map(doctor => (
                        <button
                          key={doctor.id}
                          onClick={() => handleSlotSelect(slot, doctor)}
                          className={`w-full p-3 rounded-lg text-sm transition-all ${
                            selectedSlot?.start_time === slot.start_time && selectedDoctor?.id === doctor.id
                              ? 'bg-[#2A9FCB] text-white'
                              : 'bg-white border border-gray-200 hover:border-[#2A9FCB]'
                          }`}
                        >
                          <User className="w-4 h-4 inline mr-1" />
                          {doctor.name}
                        </button>
                      ))
                    ) : (
                      <div className="w-full p-3 rounded-lg text-sm bg-gray-100 text-gray-400 text-center">
                        Non disponibile
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nessuno slot disponibile per questa data
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Orari disponibili: 12:30-13:00 e 18:30-19:00 (slot da 10 minuti)
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step >= 3 && selectedSlot && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo del consulto
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Descrivi brevemente il motivo del videoconsulto..."
              className="w-full p-3 rounded-lg border border-gray-200 resize-none focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
              rows={3}
              data-testid="reason-input"
            />

            {/* Summary */}
            <div className="mt-4 p-4 bg-[#2A9FCB]/5 rounded-xl border border-[#2A9FCB]/20">
              <h4 className="font-bold text-[#2A9FCB] mb-2">Riepilogo Prenotazione</h4>
              <div className="space-y-1 text-sm">
                <p><strong>Data:</strong> {formatDate(selectedDate)}</p>
                <p><strong>Orario:</strong> {selectedSlot.start_time} - {selectedSlot.end_time}</p>
                <p><strong>Medico:</strong> {selectedDoctor?.name}</p>
                <p><strong>Costo:</strong> <span className="text-green-600 font-bold">GRATUITO</span></p>
              </div>
            </div>

            <button
              onClick={handleBook}
              disabled={loading || !reason.trim()}
              className="w-full mt-4 py-3 bg-[#2A9FCB] text-white rounded-xl font-bold hover:bg-[#1C729E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-booking-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              Conferma Prenotazione
            </button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="rounded-xl p-5 bg-amber-50 border border-amber-200">
        <h4 className="font-bold text-amber-800 mb-2">ℹ️ Come funziona</h4>
        <ul className="text-sm text-amber-700 space-y-2">
          <li>• I videoconsulti sono <strong>gratuiti</strong> e durano 10 minuti</li>
          <li>• Potrai entrare nella stanza video 5 minuti prima dell'orario</li>
          <li>• Assicurati di avere webcam e microfono funzionanti</li>
          <li>• Riceverai un promemoria via email e SMS</li>
        </ul>
      </div>
    </div>
  );
};

export default TelemedicineBooking;
